import { create } from "zustand";
import type { Tenant, UserRole } from "@/lib/types";

// ---------------------------------------------------------------------------
// Local-storage keys
// ---------------------------------------------------------------------------
const LS_USER = "concierge:user";
const LS_TOKEN = "concierge:token";
const LS_TENANT = "concierge:tenant";
const LS_LAST_ACTIVITY = "concierge:lastActivity";

// Session timeout: 30 minutes of inactivity
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// ---------------------------------------------------------------------------
// UI Permission mapping (subset — mirrors worker's ROLE_PERMISSIONS)
// ---------------------------------------------------------------------------
const ROLE_CAN_WRITE: Record<string, boolean> = {
  platform_admin: true,
  tenant_owner: true,
  tenant_admin: true,
  tenant_manager: true,
  owner: true,
  admin: true,
};

/** Check if the current user's role allows write operations. */
export function canWrite(role: UserRole | undefined | null): boolean {
  return !!role && !!ROLE_CAN_WRITE[role];
}

/** Check if the current user's role allows team management. */
export function canManageTeam(role: UserRole | undefined | null): boolean {
  return role === "platform_admin" || role === "tenant_owner" || role === "tenant_admin" || role === "owner";
}

function loadJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

interface AuthState {
  /** Currently signed-in user */
  user: { email: string; name: string; role: UserRole } | null;
  /** JWT from Cloudflare Access / auth provider */
  token: string | null;
  /** Active tenant context — set during login / tenant switch */
  tenant: Tenant | null;
  /** Loading flag */
  loading: boolean;
  /** Actions */
  setUser: (user: AuthState["user"], token: string) => void;
  setTenant: (tenant: Tenant) => void;
  touchActivity: () => void;
  isSessionExpired: () => boolean;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: loadJSON<AuthState["user"]>(LS_USER),
  token: localStorage.getItem(LS_TOKEN),
  tenant: loadJSON<Tenant>(LS_TENANT),
  loading: false,

  setUser: (user, token) => {
    localStorage.setItem(LS_USER, JSON.stringify(user));
    localStorage.setItem(LS_TOKEN, token);
    localStorage.setItem(LS_LAST_ACTIVITY, String(Date.now()));
    set({ user, token });
  },

  setTenant: (tenant) => {
    localStorage.setItem(LS_TENANT, JSON.stringify(tenant));
    set({ tenant });
  },

  touchActivity: () => {
    localStorage.setItem(LS_LAST_ACTIVITY, String(Date.now()));
  },

  isSessionExpired: () => {
    if (!get().token) return false;
    const last = Number(localStorage.getItem(LS_LAST_ACTIVITY) ?? "0");
    return last > 0 && Date.now() - last > SESSION_TIMEOUT_MS;
  },

  logout: () => {
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_TENANT);
    localStorage.removeItem(LS_LAST_ACTIVITY);
    set({ user: null, token: null, tenant: null });
  },
}));
